// Athlete-side match-record ledger editor (Wave 2): tennis + wrestling.
// Rendered by GameStatsPage for match_record sports. Tennis logs matches with a
// derived Singles/Doubles W-L summary; wrestling logs matches (per-style W-L +
// bonus rate) and tournament placements. Records are server-derived — the client
// re-fetches after each write and never recomputes them.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import Button from '../Button';
import ConfirmModal from '../ConfirmModal';
import VerifiedFlame from '../VerifiedFlame';
import { resolveGetConfig } from './editorConfig';
import './AthleteEditor.css';

const currentYear = () => new Date().getFullYear();
const yearOptions = () => Array.from({ length: 8 }, (_, i) => currentYear() - i);
const RESULT = [{ value: 'win', label: 'Win' }, { value: 'loss', label: 'Loss' }];
const DISCIPLINE = [{ value: 'singles', label: 'Singles' }, { value: 'doubles', label: 'Doubles' }];
const TENNIS_LEVEL = [
  { value: 'usta_national', label: 'USTA National' }, { value: 'usta_sectional', label: 'USTA Sectional' },
  { value: 'itf', label: 'ITF' }, { value: 'other', label: 'Other' },
];
const STYLE = [
  { value: 'folkstyle', label: 'Folkstyle' }, { value: 'freestyle', label: 'Freestyle' }, { value: 'greco', label: 'Greco-Roman' },
];
const METHOD = [
  { value: 'fall', label: 'Fall' }, { value: 'tech_fall', label: 'Tech Fall' },
  { value: 'major_decision', label: 'Major Decision' }, { value: 'decision', label: 'Decision' }, { value: 'other', label: 'Other' },
];
const labelOf = (opts, v) => (opts.find(o => o.value === v) || {}).label || v;
const wl = (line) => `${(line && line.wins) || 0}–${(line && line.losses) || 0}`;

export default function MatchRecordEditor({ sportSlug, getConfig: getConfigProp }) {
  const getConfig = resolveGetConfig(getConfigProp);
  if (sportSlug === 'tennis') return <TennisEditor getConfig={getConfig} />;
  if (sportSlug === 'wrestling') return <WrestlingEditor getConfig={getConfig} />;
  return null;
}

// Shared confirm-on-edit-verified helper.
function useVerifiedGuard() {
  const [confirmAction, setConfirmAction] = useState(null);
  const guard = (isVerified, proceed, noun) => {
    if (isVerified) {
      setConfirmAction({
        title: `Unverify ${noun}`,
        message: `This ${noun.toLowerCase()} is currently verified. Editing it will clear the verification, and it can only be re-verified by ProZpects staff or a Partner.`,
        onConfirm: () => { setConfirmAction(null); proceed(); },
      });
    } else proceed();
  };
  const modal = confirmAction && (
    <ConfirmModal
      title={confirmAction.title} message={confirmAction.message} confirmLabel="Continue" danger
      onConfirm={confirmAction.onConfirm} onCancel={() => setConfirmAction(null)}
    />
  );
  return { guard, modal };
}

function Toast({ message, type }) {
  if (!message) return null;
  return <div className={`settings-toast ${type === 'error' ? 'settings-toast--error' : ''}`} role="alert">{message}</div>;
}

// ---------------------------------------------------------------- Tennis

const EMPTY_TENNIS = {
  discipline: '', result: '', opponent: '', opponent_utr: '', tournament_name: '',
  level: '', round: '', match_date: '', season_year: '',
};

function TennisEditor({ getConfig }) {
  const { API_URL } = useAuth();
  const [matches, setMatches] = useState([]);
  const [records, setRecords] = useState({ singles: {}, doubles: {} });
  const [form, setForm] = useState(EMPTY_TENNIS);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState(''); const [messageType, setMessageType] = useState('');
  const { guard, modal } = useVerifiedGuard();

  useEffect(() => { fetchMatches(); /* eslint-disable-next-line */ }, []);
  const fetchMatches = async () => {
    try {
      const res = await axios.get(`${API_URL}/tennis-matches/me`, getConfig());
      setMatches(Array.isArray(res.data?.matches) ? res.data.matches : []);
      setRecords(res.data?.records || { singles: {}, doubles: {} });
    } catch (err) { console.error('Error fetching matches:', err); }
  };
  const showMsg = (t, ty = '') => { setMessage(t); setMessageType(ty); setTimeout(() => setMessage(''), ty === 'error' ? 4000 : 3000); };
  const setField = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const reset = () => { setForm(EMPTY_TENNIS); setEditingId(null); };

  const doSave = async () => {
    const seasonYear = form.season_year ? Number(form.season_year) : (form.match_date ? Number(form.match_date.slice(0, 4)) : null);
    const payload = {
      discipline: form.discipline, result: form.result,
      opponent: form.opponent.trim() || null,
      opponent_utr: form.opponent_utr !== '' ? Number(form.opponent_utr) : null,
      tournament_name: form.tournament_name.trim() || null,
      level: form.level || null, round: form.round.trim() || null,
      match_date: form.match_date || null,
      season_year: Number.isFinite(seasonYear) ? seasonYear : null,
    };
    try {
      if (editingId) await axios.patch(`${API_URL}/tennis-matches/me/${editingId}`, payload, getConfig());
      else await axios.post(`${API_URL}/tennis-matches/me`, payload, getConfig());
      reset(); fetchMatches(); showMsg(editingId ? 'Match updated' : 'Match added');
    } catch (err) { showMsg('Error saving match', 'error'); }
  };
  const save = () => {
    if (!form.discipline || !form.result) { showMsg('Pick discipline and result', 'error'); return; }
    guard(editingId && matches.find(m => m.id === editingId)?.verified, doSave, 'Match');
  };
  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({
      discipline: m.discipline || '', result: m.result || '', opponent: m.opponent || '',
      opponent_utr: m.opponent_utr != null ? String(m.opponent_utr) : '',
      tournament_name: m.tournament_name || '', level: m.level || '', round: m.round || '',
      match_date: m.match_date || '', season_year: m.season_year || '',
    });
  };
  const remove = async (id) => {
    try { await axios.delete(`${API_URL}/tennis-matches/me/${id}`, getConfig()); if (editingId === id) reset(); fetchMatches(); showMsg('Match removed'); }
    catch (err) { console.error('Error deleting match:', err); }
  };

  const groups = useMemo(() => ['singles', 'doubles']
    .map(d => ({ d, rows: matches.filter(m => m.discipline === d) }))
    .filter(g => g.rows.length), [matches]);

  return (
    <>
      <Toast message={message} type={messageType} />
      <div className="settings-section">
        <h2 className="settings-section__title">Match Record</h2>
        <p className="settings-section__desc">
          Log each match — win or loss — so coaches see your record and level of competition.
          Enter your UTR under Measurables.
        </p>

        <div className="ae-record-summary">
          <div className="ae-record-tile"><span className="ae-record-tile__cap">Singles</span><span className="ae-record-tile__val">{wl(records.singles?.career)}</span></div>
          <div className="ae-record-tile"><span className="ae-record-tile__cap">Doubles</span><span className="ae-record-tile__val">{wl(records.doubles?.career)}</span></div>
        </div>

        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 160 }}>
            <label className="form-field__label">Type</label>
            <select className="form-field__select" value={form.discipline} onChange={(e) => setField({ discipline: e.target.value })} data-testid="tennis_discipline">
              <option value="">Type…</option>{DISCIPLINE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ maxWidth: 140 }}>
            <label className="form-field__label">Result</label>
            <select className="form-field__select" value={form.result} onChange={(e) => setField({ result: e.target.value })} data-testid="tennis_result">
              <option value="">Result…</option>{RESULT.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label">Opponent</label>
            <input type="text" className="form-field__input" value={form.opponent} onChange={(e) => setField({ opponent: e.target.value })} placeholder="Name" data-testid="tennis_opponent" />
          </div>
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label">Opp. UTR</label>
            <input type="number" step="0.1" className="form-field__input" value={form.opponent_utr} onChange={(e) => setField({ opponent_utr: e.target.value })} placeholder="e.g., 9.2" data-testid="tennis_opponent_utr" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label">Tournament</label>
            <input type="text" className="form-field__input" value={form.tournament_name} onChange={(e) => setField({ tournament_name: e.target.value })} placeholder="e.g., State Open" data-testid="tennis_tournament" />
          </div>
          <div className="form-field" style={{ maxWidth: 200 }}>
            <label className="form-field__label">Level</label>
            <select className="form-field__select" value={form.level} onChange={(e) => setField({ level: e.target.value })} data-testid="tennis_level">
              <option value="">Level…</option>{TENNIS_LEVEL.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label">Round</label>
            <input type="text" className="form-field__input" value={form.round} onChange={(e) => setField({ round: e.target.value })} placeholder="QF" data-testid="tennis_round" />
          </div>
          <div className="form-field" style={{ maxWidth: 170 }}>
            <label className="form-field__label">Date</label>
            <input type="date" className="form-field__input" value={form.match_date} onChange={(e) => setField({ match_date: e.target.value })} />
          </div>
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label">Season</label>
            <select className="form-field__select" value={form.season_year} onChange={(e) => setField({ season_year: e.target.value })}>
              <option value="">Year…</option>{yearOptions().map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" variant="accent" size="sm" onClick={save} data-testid="tennis_save">{editingId ? 'Save Changes' : 'Add Match'}</Button>
          {editingId && <Button type="button" variant="ghost" size="sm" onClick={reset}>Cancel</Button>}
        </div>

        {matches.length === 0 && <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: 12 }}>No matches logged yet.</p>}
        {groups.map(g => (
          <div key={g.d} className="ae-mark-group" style={{ marginTop: 14 }}>
            <div className="ae-mark-group__head">{labelOf(DISCIPLINE, g.d)}</div>
            {g.rows.map(m => (
              <div key={m.id} className="accolade-row">
                <div className="accolade-row__body">
                  <span className="accolade-row__title">
                    <span className={`ae-result-chip ae-result-chip--${m.result}`}>{m.result === 'win' ? 'W' : 'L'}</span>
                    {m.opponent || 'Unknown opponent'}
                    {m.verified && <VerifiedFlame />}
                  </span>
                  <span className="accolade-row__meta">
                    {m.tournament_name && <span className="accolade-row__src">{m.tournament_name}</span>}
                    {m.level && <span className="accolade-chip">{labelOf(TENNIS_LEVEL, m.level)}</span>}
                    {m.round && <span className="accolade-row__src">{m.round}</span>}
                    {m.match_date && <span className="accolade-row__src">{m.match_date}</span>}
                  </span>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(m)}>Edit</Button>
                <Button type="button" variant="danger" size="sm" onClick={() => remove(m.id)}>Delete</Button>
              </div>
            ))}
          </div>
        ))}
      </div>
      {modal}
    </>
  );
}

// ---------------------------------------------------------------- Wrestling

const EMPTY_WMATCH = {
  style: '', result: '', weight_class: '', method: '', opponent: '', opponent_rank: '',
  event_name: '', event_date: '', season_year: '',
};
const EMPTY_WPLACE = { style: '', weight_class: '', event_name: '', place: '', event_date: '', season_year: '' };

function WrestlingEditor({ getConfig }) {
  const { API_URL } = useAuth();
  const [matches, setMatches] = useState([]);
  const [records, setRecords] = useState({ folkstyle: {}, freestyle: {}, greco: {} });
  const [placements, setPlacements] = useState([]);
  const [mForm, setMForm] = useState(EMPTY_WMATCH);
  const [mEditId, setMEditId] = useState(null);
  const [pForm, setPForm] = useState(EMPTY_WPLACE);
  const [pEditId, setPEditId] = useState(null);
  const [message, setMessage] = useState(''); const [messageType, setMessageType] = useState('');
  const { guard, modal } = useVerifiedGuard();

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);
  const fetchAll = async () => {
    try {
      const [mRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/wrestling/matches/me`, getConfig()).catch(() => ({ data: { matches: [], records: {} } })),
        axios.get(`${API_URL}/wrestling/placements/me`, getConfig()).catch(() => ({ data: { placements: [] } })),
      ]);
      setMatches(Array.isArray(mRes.data?.matches) ? mRes.data.matches : []);
      setRecords(mRes.data?.records || { folkstyle: {}, freestyle: {}, greco: {} });
      setPlacements(Array.isArray(pRes.data?.placements) ? pRes.data.placements : []);
    } catch (err) { console.error('Error fetching wrestling:', err); }
  };
  const showMsg = (t, ty = '') => { setMessage(t); setMessageType(ty); setTimeout(() => setMessage(''), ty === 'error' ? 4000 : 3000); };

  // Matches
  const setMField = (patch) => setMForm(prev => ({ ...prev, ...patch }));
  const mReset = () => { setMForm(EMPTY_WMATCH); setMEditId(null); };
  const mDoSave = async () => {
    const sy = mForm.season_year ? Number(mForm.season_year) : (mForm.event_date ? Number(mForm.event_date.slice(0, 4)) : null);
    const payload = {
      style: mForm.style, result: mForm.result, weight_class: mForm.weight_class.trim(),
      method: mForm.method || null, opponent: mForm.opponent.trim() || null,
      opponent_rank: mForm.opponent_rank !== '' ? Number(mForm.opponent_rank) : null,
      event_name: mForm.event_name.trim() || null, event_date: mForm.event_date || null,
      season_year: Number.isFinite(sy) ? sy : null,
    };
    try {
      if (mEditId) await axios.patch(`${API_URL}/wrestling/matches/me/${mEditId}`, payload, getConfig());
      else await axios.post(`${API_URL}/wrestling/matches/me`, payload, getConfig());
      mReset(); fetchAll(); showMsg(mEditId ? 'Match updated' : 'Match added');
    } catch (err) { showMsg('Error saving match', 'error'); }
  };
  const mSave = () => {
    if (!mForm.style || !mForm.result || !mForm.weight_class.trim()) { showMsg('Style, result and weight are required', 'error'); return; }
    guard(mEditId && matches.find(m => m.id === mEditId)?.verified, mDoSave, 'Match');
  };
  const mStartEdit = (m) => {
    setMEditId(m.id);
    setMForm({
      style: m.style || '', result: m.result || '', weight_class: m.weight_class || '',
      method: m.method || '', opponent: m.opponent || '', opponent_rank: m.opponent_rank != null ? String(m.opponent_rank) : '',
      event_name: m.event_name || '', event_date: m.event_date || '', season_year: m.season_year || '',
    });
  };
  const mRemove = async (id) => {
    try { await axios.delete(`${API_URL}/wrestling/matches/me/${id}`, getConfig()); if (mEditId === id) mReset(); fetchAll(); showMsg('Match removed'); }
    catch (err) { console.error(err); }
  };

  // Placements
  const setPField = (patch) => setPForm(prev => ({ ...prev, ...patch }));
  const pReset = () => { setPForm(EMPTY_WPLACE); setPEditId(null); };
  const pDoSave = async () => {
    const sy = pForm.season_year ? Number(pForm.season_year) : (pForm.event_date ? Number(pForm.event_date.slice(0, 4)) : null);
    const payload = {
      style: pForm.style, weight_class: pForm.weight_class.trim(), event_name: pForm.event_name.trim() || null,
      place: pForm.place !== '' ? Number(pForm.place) : null, event_date: pForm.event_date || null,
      season_year: Number.isFinite(sy) ? sy : null,
    };
    try {
      if (pEditId) await axios.patch(`${API_URL}/wrestling/placements/me/${pEditId}`, payload, getConfig());
      else await axios.post(`${API_URL}/wrestling/placements/me`, payload, getConfig());
      pReset(); fetchAll(); showMsg(pEditId ? 'Placement updated' : 'Placement added');
    } catch (err) { showMsg('Error saving placement', 'error'); }
  };
  const pSave = () => {
    if (!pForm.style || !pForm.weight_class.trim() || !(Number(pForm.place) > 0)) { showMsg('Style, weight and place are required', 'error'); return; }
    guard(pEditId && placements.find(p => p.id === pEditId)?.verified, pDoSave, 'Placement');
  };
  const pStartEdit = (p) => {
    setPEditId(p.id);
    setPForm({
      style: p.style || '', weight_class: p.weight_class || '', event_name: p.event_name || '',
      place: p.place != null ? String(p.place) : '', event_date: p.event_date || '', season_year: p.season_year || '',
    });
  };
  const pRemove = async (id) => {
    try { await axios.delete(`${API_URL}/wrestling/placements/me/${id}`, getConfig()); if (pEditId === id) pReset(); fetchAll(); showMsg('Placement removed'); }
    catch (err) { console.error(err); }
  };

  const activeStyles = ['folkstyle', 'freestyle', 'greco'].filter(s => {
    const c = records[s]?.career; return c && (c.wins || c.losses);
  });
  const ordinal = (n) => {
    if (n == null) return '';
    const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return (
    <>
      <Toast message={message} type={messageType} />
      <div className="settings-section">
        <h2 className="settings-section__title">Wrestling</h2>
        <p className="settings-section__desc">
          Log matches with style, weight, and method — bonus wins (fall / tech / major) drive your bonus rate.
          Add tournament placements below.
        </p>

        {activeStyles.length > 0 && (
          <div className="ae-record-summary">
            {activeStyles.map(s => {
              const c = records[s].career;
              return (
                <div key={s} className="ae-record-tile">
                  <span className="ae-record-tile__cap">{labelOf(STYLE, s)}</span>
                  <span className="ae-record-tile__val">{wl(c)}</span>
                  <span className="ae-record-tile__sub">{Math.round((c.bonus_rate || 0) * 100)}% bonus</span>
                </div>
              );
            })}
          </div>
        )}

        <h3 className="ae-subhead">Matches</h3>
        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 160 }}>
            <label className="form-field__label">Style</label>
            <select className="form-field__select" value={mForm.style} onChange={(e) => setMField({ style: e.target.value })} data-testid="wrestling_style">
              <option value="">Style…</option>{STYLE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ maxWidth: 130 }}>
            <label className="form-field__label">Result</label>
            <select className="form-field__select" value={mForm.result} onChange={(e) => setMField({ result: e.target.value })} data-testid="wrestling_result">
              <option value="">Result…</option>{RESULT.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ maxWidth: 110 }}>
            <label className="form-field__label">Weight</label>
            <input type="text" className="form-field__input" value={mForm.weight_class} onChange={(e) => setMField({ weight_class: e.target.value })} placeholder="126" data-testid="wrestling_weight_class" />
          </div>
          <div className="form-field" style={{ maxWidth: 180 }}>
            <label className="form-field__label">Method</label>
            <select className="form-field__select" value={mForm.method} onChange={(e) => setMField({ method: e.target.value })} data-testid="wrestling_method">
              <option value="">Method…</option>{METHOD.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label">Opponent</label>
            <input type="text" className="form-field__input" value={mForm.opponent} onChange={(e) => setMField({ opponent: e.target.value })} placeholder="Name" data-testid="wrestling_opponent" />
          </div>
          <div className="form-field" style={{ maxWidth: 110 }}>
            <label className="form-field__label">Opp. rank</label>
            <input type="number" className="form-field__input" value={mForm.opponent_rank} onChange={(e) => setMField({ opponent_rank: e.target.value })} placeholder="3" data-testid="wrestling_opponent_rank" />
          </div>
          <div className="form-field">
            <label className="form-field__label">Event</label>
            <input type="text" className="form-field__input" value={mForm.event_name} onChange={(e) => setMField({ event_name: e.target.value })} placeholder="Districts" data-testid="wrestling_event_name" />
          </div>
          <div className="form-field" style={{ maxWidth: 170 }}>
            <label className="form-field__label">Date</label>
            <input type="date" className="form-field__input" value={mForm.event_date} onChange={(e) => setMField({ event_date: e.target.value })} />
          </div>
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label">Season</label>
            <select className="form-field__select" value={mForm.season_year} onChange={(e) => setMField({ season_year: e.target.value })}>
              <option value="">Year…</option>{yearOptions().map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" variant="accent" size="sm" onClick={mSave} data-testid="wrestling_match_save">{mEditId ? 'Save Changes' : 'Add Match'}</Button>
          {mEditId && <Button type="button" variant="ghost" size="sm" onClick={mReset}>Cancel</Button>}
        </div>
        {matches.length === 0 && <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: 12 }}>No matches logged yet.</p>}
        <div className="ae-mark-group" style={{ marginTop: 12 }}>
          {matches.map(m => (
            <div key={m.id} className="accolade-row">
              <div className="accolade-row__body">
                <span className="accolade-row__title">
                  <span className={`ae-result-chip ae-result-chip--${m.result}`}>{m.result === 'win' ? 'W' : 'L'}</span>
                  {m.opponent || 'Unknown opponent'}
                  {m.verified && <VerifiedFlame />}
                </span>
                <span className="accolade-row__meta">
                  <span className="accolade-chip">{labelOf(STYLE, m.style)}</span>
                  {m.weight_class && <span className="accolade-row__src">{m.weight_class}</span>}
                  {m.method && <span className="accolade-row__src">{labelOf(METHOD, m.method)}</span>}
                  {m.event_name && <span className="accolade-row__src">{m.event_name}</span>}
                  {m.event_date && <span className="accolade-row__src">{m.event_date}</span>}
                </span>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => mStartEdit(m)}>Edit</Button>
              <Button type="button" variant="danger" size="sm" onClick={() => mRemove(m.id)}>Delete</Button>
            </div>
          ))}
        </div>

        <h3 className="ae-subhead" style={{ marginTop: 20 }}>Placements</h3>
        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 160 }}>
            <label className="form-field__label">Style</label>
            <select className="form-field__select" value={pForm.style} onChange={(e) => setPField({ style: e.target.value })} data-testid="wrestling_placement_style">
              <option value="">Style…</option>{STYLE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-field" style={{ maxWidth: 110 }}>
            <label className="form-field__label">Weight</label>
            <input type="text" className="form-field__input" value={pForm.weight_class} onChange={(e) => setPField({ weight_class: e.target.value })} placeholder="126" data-testid="wrestling_placement_weight_class" />
          </div>
          <div className="form-field" style={{ maxWidth: 100 }}>
            <label className="form-field__label">Place</label>
            <input type="number" className="form-field__input" value={pForm.place} onChange={(e) => setPField({ place: e.target.value })} placeholder="1" data-testid="wrestling_place" />
          </div>
          <div className="form-field">
            <label className="form-field__label">Event</label>
            <input type="text" className="form-field__input" value={pForm.event_name} onChange={(e) => setPField({ event_name: e.target.value })} placeholder="State" data-testid="wrestling_placement_event_name" />
          </div>
          <div className="form-field" style={{ maxWidth: 170 }}>
            <label className="form-field__label">Date</label>
            <input type="date" className="form-field__input" value={pForm.event_date} onChange={(e) => setPField({ event_date: e.target.value })} />
          </div>
          <div className="form-field" style={{ maxWidth: 120 }}>
            <label className="form-field__label">Season</label>
            <select className="form-field__select" value={pForm.season_year} onChange={(e) => setPField({ season_year: e.target.value })}>
              <option value="">Year…</option>{yearOptions().map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" variant="accent" size="sm" onClick={pSave} data-testid="wrestling_placement_save">{pEditId ? 'Save Changes' : 'Add Placement'}</Button>
          {pEditId && <Button type="button" variant="ghost" size="sm" onClick={pReset}>Cancel</Button>}
        </div>
        {placements.length === 0 && <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: 12 }}>No placements logged yet.</p>}
        <div className="ae-mark-group" style={{ marginTop: 12 }}>
          {placements.map(p => (
            <div key={p.id} className="accolade-row">
              <div className="accolade-row__body">
                <span className="accolade-row__title">
                  <span className="ae-result-chip ae-result-chip--place">{ordinal(p.place)}</span>
                  {p.event_name || 'Event'}
                  {p.verified && <VerifiedFlame />}
                </span>
                <span className="accolade-row__meta">
                  <span className="accolade-chip">{labelOf(STYLE, p.style)}</span>
                  {p.weight_class && <span className="accolade-row__src">{p.weight_class}</span>}
                  {p.event_date && <span className="accolade-row__src">{p.event_date}</span>}
                </span>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => pStartEdit(p)}>Edit</Button>
              <Button type="button" variant="danger" size="sm" onClick={() => pRemove(p.id)}>Delete</Button>
            </div>
          ))}
        </div>
      </div>
      {modal}
    </>
  );
}
